import React from "react";
import styled from "styled-components";
import {
  layout,
  color,
  position,
  variant,
  LayoutProps,
  ColorProps,
  PositionProps,
} from "styled-system";
import { useOverlayTriggerState } from "@react-stately/overlays";
import { useButton } from "@react-aria/button";
import { useOverlayTrigger } from "@react-aria/overlays";
import css from "@styled-system/css";

import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import Icon from "../Icon/Icon";
import MobileMenu from "../ConstellationHeader/MobileMenu";
import Logo from "../Logo/Logo";
import NavItem from "./NavItem";
import { MenuLink } from "../Navigation/MenuLink.type";
import variants from "./variants";

export interface CareersHeaderProps extends ColorProps, PositionProps {
  navItems: MenuLink[];
  variant: "transparent" | "white";
  idHighlighted?: string;
}

const Header = styled.header<
  ColorProps & PositionProps & { variant: "transparent" | "white" }
>`
  display: flex;
  align-items: center;
  width: 100%;
  ${css({
    fontFamily: "normal",
  })}
  ${variant({ variants })}
  ${color}
  ${position}
`;

const TopLevelNav = styled.ul<LayoutProps>`
  display: flex;
  list-style-type: none;
  ${css({ mt: 4, mb: 4 })}
  ${layout}
`;

const HamburgerButton = styled.button<LayoutProps>`
  background: transparent;
  border: none;
  padding: 0;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
  ${layout}
`;

const CareersHeader = ({
  navItems,
  variant,
  idHighlighted,
  color,
  ...props
}: CareersHeaderProps) => {
  const menuButtons = navItems
    .filter((item) => item.isVisible)
    .map((item: MenuLink) => {
      return (
        <NavItem
          highlighted={item.id === idHighlighted}
          key={item.id}
          item={item}
        ></NavItem>
      );
    });

  const state = useOverlayTriggerState({});
  const triggerRef = React.useRef(null);
  const overlayRef = React.useRef(null);

  let { triggerProps, overlayProps } = useOverlayTrigger(
    { type: "dialog" },
    state,
    triggerRef
  );

  let { buttonProps } = useButton(
    {
      onPress: () => state.open(),
    },
    triggerRef
  );

  return (
    <Header color={color as string} variant={variant} {...props}>
      {state.isOpen && (
        <MobileMenu
          navItems={navItems}
          onClose={state.close}
          isOpen={state.isOpen}
          ref={overlayRef}
          customLogo={<Logo />}
          itemsColor="secondary-nav-blue"
          {...overlayProps}
        ></MobileMenu>
      )}

      <ResponsiveLayout
        height={{ default: "58px", desktop: "123px" }}
        as="nav"
        role="navigation"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <a
          href="/"
          target="_top"
          rel="noopener noreferrer"
          aria-label="BCA Logo"
        >
          <Logo />
        </a>
        <HamburgerButton
          {...buttonProps}
          {...triggerProps}
          ref={triggerRef}
          display={{ _: "initial", desktop: "none" }}
        >
          <Icon
            name="bars"
            weight="regular"
            color={color ? (color as string) : variants[variant].color}
            height={18}
            width={18}
          />
        </HamburgerButton>

        <TopLevelNav display={{ _: "none", desktop: "flex" }}>
          {menuButtons}
        </TopLevelNav>
      </ResponsiveLayout>
    </Header>
  );
};

export default CareersHeader;
